import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import './LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('spoc');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const roles = [
    { value: 'spoc', label: 'SPOC (Single Point of Contact)', icon: '🏫', desc: 'Upload certificates, view analytics, manage alerts for your institution.' },
    { value: 'admin', label: 'Administrator', icon: '⚙️', desc: 'Full database access, analytics, and system administration.' },
    { value: 'hr', label: 'HR/Recruiter', icon: '👔', desc: 'Verify certificate authenticity for recruitment purposes.' },
    { value: 'student', label: 'Certificate Student', icon: '🎓', desc: 'View and download your issued certificates.' }
  ];

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      if (localStorage.getItem('isAuthenticated')) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        console.log('🔍 Checking existing auth, user:', user);
        console.log('🔍 User role:', user.role);
        
        if (user.role === 'spoc') {
          console.log('✅ Redirecting to /spoc-dashboard');
          navigate('/spoc-dashboard');
        } else if (user.role === 'admin') {
          console.log('✅ Redirecting to /admin');
          navigate('/admin');
        } else if (user.role === 'hr') {
          console.log('✅ Redirecting to /hr/verify');
          navigate('/hr/verify');
        } else if (user.role === 'student') {
          console.log('✅ Redirecting to /student-dashboard');
          navigate('/student-dashboard');
        } else {
          console.warn('⚠️ Unknown role or no role set:', user.role);
        }
      }
    };
    checkAuth();
  }, [navigate]);

  // Load saved email if remember me was checked
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const redirectToRoleDashboard = (userRole) => {
    console.log('🚀 redirectToRoleDashboard called with role:', userRole);
    switch(userRole) {
      case 'spoc':
        console.log('➡️ Navigating to /spoc-dashboard');
        navigate('/spoc-dashboard');
        break;
      case 'admin':
        console.log('➡️ Navigating to /admin');
        navigate('/admin');
        break;
      case 'hr':
        console.log('➡️ Navigating to /hr/verify');
        navigate('/hr/verify');
        break;
      case 'student':
        console.log('➡️ Navigating to /student-dashboard');
        navigate('/student-dashboard');
        break;
      default:
        console.warn('⚠️ Unknown role, navigating to home:', userRole);
        navigate('/');
    }
  };

  const mockLogin = async () => {
    // Mock authentication for development
    console.log('🧪 Using mock authentication');
    console.log('📝 Email:', email);
    console.log('🎭 Selected role:', role);
    
    // Role-specific test credentials
    const validCredentials = [
      { email: 'spoc@gmail.com', password: '123456', role: 'spoc' },
      { email: 'admin@gmail.com', password: '123456', role: 'admin' },
      { email: 'hr@gmail.com', password: '123456', role: 'hr' },
      { email: 'student@gmail.com', password: '123456', role: 'student' },
      // Legacy test credential (works for any role)
      { email: 'test@gmail.com', password: '123456', role: null }
    ];

    const credential = validCredentials.find(c => c.email === email && c.password === password);
    
    if (credential) {
      // Use credential's role if specified, otherwise use selected role
      const userRole = credential.role || role;
      
      const userData = {
        email: email,
        role: userRole,
        organization: userRole === 'spoc' ? 'Birla Institute of Technology Patna' : 'Test Organization',
        institutionId: userRole === 'spoc' ? 'birla_institute_technology_patna' : 'test_org'
      };
      
      console.log('✅ Mock login successful');
      console.log('💾 Saving user data:', userData);
      
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(userData));

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      }

      console.log('🎯 Redirecting to dashboard for role:', userRole);
      redirectToRoleDashboard(userRole);
    } else {
      throw new Error('Invalid credentials. Use spoc@gmail.com, admin@gmail.com, hr@gmail.com, or student@gmail.com with password 123456.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    try {
      // Try Supabase authentication first
      if (supabase) {
        const { data, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (authError) {
          // If Supabase auth fails, try mock authentication for development
          console.warn('Supabase auth failed, using mock auth:', authError.message);
          await mockLogin();
        } else if (data.user) {
          // Successful Supabase authentication
          console.log('✅ Supabase authentication successful');
          
          // Get user profile from database
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', data.user.id)
            .single();

          const userRole = profile?.role || role;
          
          console.log('🔐 Login successful');
          console.log('📊 Profile from DB:', profile);
          console.log('👤 User role:', userRole);
          console.log('🏢 Institution ID:', profile?.institution_id);
          
          // Save to localStorage
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('user', JSON.stringify({
            id: data.user.id,
            email: data.user.email,
            role: userRole,
            institutionId: profile?.institution_id,
            organization: profile?.organization,
            fullName: profile?.full_name
          }));

          if (rememberMe) {
            localStorage.setItem('rememberedEmail', email);
          } else {
            localStorage.removeItem('rememberedEmail');
          }

          console.log('🎯 Redirecting to role dashboard for:', userRole);
          // Redirect based on role
          redirectToRoleDashboard(userRole);
        }
      } else {
        // Supabase not configured, use mock authentication
        await mockLogin();
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Background Images */}
      <div className="login-background-layer">
        <img src="/mesh-gradient%202.webp" alt="" className="background-mesh" />
      </div>
      <div className="login-wave-layer">
        <img src="/hero-wave.svg" alt="" className="wave-image" />
      </div>
      <div className="login-container">
        {/* Left – Brand / Info (inspired by verifier/uploader HTML) */}
        <div className="login-left">
          <div className="login-brand">
            <div className="login-logo-circle">CT</div>
            <div className="login-brand-text">
              <h1>CertiTrust</h1>
              <p className="tagline">Secure, blockchain-backed academic credential verification</p>
            </div>
          </div>

          <h2>Sign in to continue</h2>
          <p>
            Role-based access for SPOCs, administrators, HR teams and students.
            Verify, upload and manage certificates in a single trusted platform.
          </p>

          <div className="login-left-highlights">
            <span><i></i> Instant hash-based verification against institutional database</span>
            <span><i></i> AI-powered authenticity checks for suspicious certificates</span>
            <span><i></i> IPFS-backed storage with verifiable audit trail</span>
          </div>
        </div>

        {/* Right – Form */}
        <div className="login-right">
          <h2 className="login-right-title">
            {role === 'spoc' ? 'SPOC Portal' : 'Welcome back'}
          </h2>
          <p className="login-right-subtitle">
            {role === 'spoc'
              ? 'Dedicated access for institutional SPOCs to issue and manage certificates.'
              : 'Choose your role and sign in to your CertiTrust workspace.'}
          </p>

          {/* Role Tabs */}
          <div className="login-role-tabs">
            {roles.map((r) => {
              const isActive = role === r.value;
              const base = 'login-role-tab';
              const activeClass = isActive ? ' active' : '';
              const spocClass = isActive && r.value === 'spoc' ? ' active-spoc' : '';
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={`${base}${activeClass}${spocClass}`}
                >
                  <span>{r.icon}</span>
                  <span>{r.value === 'spoc' ? 'SPOC' : r.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>

          <div className="login-panel">
            <form onSubmit={handleLogin} className="login-form">
              {/* Email Input */}
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-with-icon">
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="form-control-login"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-with-icon">
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="form-control-login"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="login-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              <div className="login-meta-row">
                <label className="login-remember">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  Remember me
                </label>
                <Link to="/forgot-password" className="login-forgot">Forgot password?</Link>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-login-primary"
              >
                {isLoading ? (
                  <>
                    <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                      <polyline points="10 17 15 12 10 7"/>
                      <line x1="15" y1="12" x2="3" y2="12"/>
                    </svg>
                    Sign In
                  </>
                )}
              </button>

              {/* Test Credentials Helper */}
              {process.env.NODE_ENV === 'development' && (
                <div className="test-credentials">
                  <small>🧪 Test Credentials (password: 123456):</small>
                  <small>SPOC: spoc@gmail.com | Admin: admin@gmail.com</small>
                  <small>HR: hr@gmail.com | Student: student@gmail.com</small>
                </div>
              )}
            </form>

            {/* Role Info */}
            {role && (
              <div className="login-role-info">
                <div className="role-info-header">
                  <span className="role-icon">{roles.find(r => r.value === role)?.icon}</span>
                  <strong>{roles.find(r => r.value === role)?.label}</strong>
                </div>
                <div className="role-info-desc">
                  {roles.find(r => r.value === role)?.desc}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="login-footer">
              Don't have an account? <Link to="/register">Register</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
